import React from 'react';
import { Check } from 'lucide-react';

// Range options configuration
export const RANGE_OPTIONS = {
  homeValue: [
    { id: 'under-800k', label: 'Under $800k', midpoint: 600000 },
    { id: '800k-1.1m', label: '$800k - $1.1M', midpoint: 950000 },
    { id: '1.1m-1.4m', label: '$1.1M - $1.4M', midpoint: 1250000 },
    { id: '1.4m-plus', label: '$1.4M+', midpoint: 1600000 },
  ],
  mortgageBalance: [
    { id: 'under-400k', label: 'Under $400k', midpoint: 250000 },
    { id: '400k-650k', label: '$400k - $650k', midpoint: 525000 },
    { id: '650k-900k', label: '$650k - $900k', midpoint: 775000 },
    { id: '900k-plus', label: '$900k+', midpoint: 1000000 },
  ],
  shadowDebt: [
    { id: 'under-15k', label: 'Under $15k', midpoint: 7500 },
    { id: '15k-50k', label: '$15k - $50k', midpoint: 32500 },
    { id: '50k-100k', label: '$50k - $100k', midpoint: 75000 },
    { id: '100k-plus', label: '$100k+', midpoint: 125000 },
  ],
  householdIncome: [
    { id: 'under-120k', label: 'Under $120k', midpoint: 90000 },
    { id: '120k-180k', label: '$120k - $180k', midpoint: 150000 },
    { id: '180k-250k', label: '$180k - $250k', midpoint: 215000 },
    { id: '250k-plus', label: '$250k+', midpoint: 300000 },
  ],
};

// Get midpoint value from selected range ID
export const getMidpointValue = (rangeType, rangeId) => {
  const options = RANGE_OPTIONS[rangeType];
  const option = options?.find(opt => opt.id === rangeId);
  return option?.midpoint || 0;
};

export const RangeSelector = ({ 
  options, 
  selectedId, 
  onSelect, 
  testIdPrefix = 'range'
}) => {
  return (
    <div className="grid grid-cols-2 gap-3 md:gap-4 w-full max-w-lg mx-auto">
      {options.map((option, index) => {
        const isSelected = selectedId === option.id;
        
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onSelect(option.id)}
            data-testid={`${testIdPrefix}-${option.id}`}
            className={`
              relative flex items-center justify-center
              p-4 md:p-5 rounded-xl border-2 
              transition-all duration-200 ease-out
              text-center font-heading font-semibold
              ${isSelected
                ? 'bg-gold-400 border-gold-400 text-navy-950 shadow-lg scale-[1.02]'
                : 'bg-white border-slate-200 text-slate-700 hover:border-gold-400/50 hover:shadow-md'
              }
            `}
          >
            {/* Selection indicator */}
            {isSelected && (
              <div className="absolute top-2 right-2">
                <Check className="w-4 h-4 text-navy-950" strokeWidth={3} />
              </div>
            )}
            
            <span className="text-base md:text-lg leading-tight">
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// Step wrapper with smooth transitions
export const RangeStep = ({
  stepNumber,
  totalSteps,
  headline,
  subtext,
  options,
  selectedId,
  onSelect,
  testIdPrefix,
  isVisible = true
}) => {
  if (!isVisible) return null;

  return (
    <div 
      className="animate-fadeIn text-center"
      data-testid={`range-step-${stepNumber}`}
    >
      {/* Step indicator */}
      <p className="text-sm font-heading uppercase tracking-widest text-slate-500 mb-3">
        Step {stepNumber} of {totalSteps}
      </p>
      
      {/* Headline */}
      <h2 className="text-2xl md:text-3xl font-heading font-semibold text-navy-950 mb-3 leading-tight">
        {headline}
      </h2>
      
      {/* Subtext */}
      {subtext && (
        <p className="text-base text-slate-600 max-w-md mx-auto mb-8">
          {subtext}
        </p>
      )}

      {/* Range buttons */}
      <RangeSelector
        options={options}
        selectedId={selectedId}
        onSelect={onSelect}
        testIdPrefix={testIdPrefix}
      />
    </div>
  );
};
